import axios from 'axios';

/** Базовый адрес «Сверки доставок» (все запросы проверяются сервером по сессии и правам роли) */
export const DELIVERY_API = '/api/v2/reports/delivery';

/** Город с терминалом, закрепленный за аптекой (created_by - логин аптеки) */
export interface CityTerminal {
  id: number;
  city_name: string;
  /** ID терминала из склада передачи КД (PP1, PP7, PP14...) */
  terminal_id: string;
  terminal_code: string;
  /** Точный адрес аптеки: по нему заказ привязывается, если терминал определить не удалось */
  address: string;
  created_by: string;
  created_at: string;
}

/** Суммы доставки за день из отчетов аптек */
export interface DaySums {
  total: number;
  /** Ключ - логин аптеки; Map, чтобы логин не мог пересечься со служебными свойствами объекта */
  byPharmacy: Map<string, number>;
}

/** Аптека, к которой можно привязать терминал */
export interface DeliveryPharmacy {
  username: string;
  pharmacyNum: string;
}

/** Поля терминала, которые заполняет оператор */
export interface TerminalFields {
  city_name: string;
  terminal_code: string;
  terminal_id: string;
  address: string;
}

/** Результат ответа сервера: при success=false бросается ошибка с текстом сервера */
function unwrap<T>(data: T & { success?: boolean; message?: string }, fallback: string): T {
  if (!data || !data.success) {
    throw new Error((data && data.message) || fallback);
  }
  return data;
}

/** Города и терминалы (право просмотра сверки) */
export async function fetchTerminals(): Promise<CityTerminal[]> {
  const res = await axios.get(`${DELIVERY_API}/terminals`);
  return unwrap<{ terminals: CityTerminal[] }>(res.data, 'Не удалось загрузить данные о городах').terminals;
}

/** Аптеки для привязки терминала (право управления терминалами) */
export async function fetchPharmacies(): Promise<DeliveryPharmacy[]> {
  const res = await axios.get(`${DELIVERY_API}/pharmacies`);
  return unwrap<{ pharmacies: DeliveryPharmacy[] }>(res.data, 'Не удалось загрузить список аптек').pharmacies;
}

/** Суммы доставки за день: общая и по каждой аптеке с терминалом */
export async function fetchDaySums(date: string): Promise<DaySums> {
  const res = await axios.get(`${DELIVERY_API}/day-sums`, { params: { date } });
  const data = unwrap<{ totalSum: number; citySums: Record<string, number> }>(res.data, 'Не удалось получить данные о доставках');

  const byPharmacy = new Map<string, number>();
  Object.keys(data.citySums || {}).forEach((login) => {
    byPharmacy.set(login, Number(data.citySums[login]) || 0);
  });
  return { total: Number(data.totalSum) || 0, byPharmacy };
}

/** Новый терминал; сервер возвращает созданную запись */
export async function createTerminal(created_by: string, fields: TerminalFields): Promise<CityTerminal> {
  const res = await axios.post(`${DELIVERY_API}/terminals`, { ...fields, created_by });
  return unwrap<{ terminal: CityTerminal }>(res.data, 'Не удалось добавить город').terminal;
}

/** Изменение терминала; аптека у записи не меняется */
export async function updateTerminal(id: number, fields: TerminalFields): Promise<CityTerminal> {
  const res = await axios.put(`${DELIVERY_API}/terminals/${id}`, fields);
  return unwrap<{ terminal: CityTerminal }>(res.data, 'Не удалось обновить город').terminal;
}

export async function deleteTerminal(id: number): Promise<void> {
  const res = await axios.delete(`${DELIVERY_API}/terminals/${id}`);
  unwrap(res.data, 'Не удалось удалить город');
}
